import { format, getISOWeek, getISOWeekYear, parse } from 'date-fns';
import { ensureUtcIso, formatRunWallClock } from './sessionTime';
import { isRunActivelySettling } from '../types/Run';

export type RunGroupMode = 'date' | 'week' | 'month';

export interface RunGroup<T = any> {
  key: string;
  label: string;
  runs: T[];
}

const UNKNOWN_KEY = 'unknown';

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// Wall-clock yyyy-MM-dd in the run's own timezone, consistent with what the
// row's datetime column displays. A run that is actively settling has no
// timestamp yet — it was recorded moments ago, so it groups under today rather
// than sinking into the collapsed "Unknown date" bucket at the very bottom,
// which is exactly where the user is not looking for a just-uploaded run.
// The test is `isRunActivelySettling`, not the wider `isRunSettling` band: a
// planned (bare CREATED) run was not recorded moments ago — it may be days
// out — so back-dating it to today would file it under the wrong day.
function wallClockDate(run: RunGroupFields): string | null {
  let wc = formatRunWallClock(run.run_ts, run.timezone, 'datetime');
  if (!wc && isRunActivelySettling(run.run_status)) {
    // A settling run has no timezone yet either (it is derived from the
    // decoded run_ts), so fall back to the viewer's own zone rather than UTC
    // — otherwise a run uploaded either side of midnight lands a day out.
    const zone = run.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    wc = formatRunWallClock(new Date().toISOString(), zone, 'datetime');
  }
  const dateStr = wc ? wc.split(' ')[0] : null;
  return dateStr && DATE_RE.test(dateStr) ? dateStr : null;
}

function bucketFor(dateStr: string, mode: RunGroupMode): { key: string; label: string } {
  const d = parse(dateStr, 'yyyy-MM-dd', new Date());
  if (mode === 'week') {
    const week = getISOWeek(d);
    const year = getISOWeekYear(d);
    return { key: `${year}-W${String(week).padStart(2, '0')}`, label: `Week ${week}, ${year}` };
  }
  if (mode === 'month') {
    return { key: format(d, 'yyyy-MM'), label: format(d, 'MMMM yyyy') };
  }
  return { key: dateStr, label: format(d, 'EEE d MMM yyyy') };
}

export interface RunGroupFields {
  run_ts?: string | null;
  run_status?: number | null;
  timezone?: string | null;
}

// Bucket runs by wall-clock date / ISO week / month. Groups come back newest
// first (unknown dates last), runs newest-first within each group. Settling
// runs sort to the top of today's group — their year-9999 sentinel parses
// larger than any real timestamp, which is the order the user wants anyway.
export function groupRuns<T extends RunGroupFields>(
  runs: T[],
  mode: RunGroupMode,
): RunGroup<T>[] {
  const buckets = new Map<string, { label: string; runs: T[] }>();
  for (const run of runs) {
    const dateStr = wallClockDate(run);
    const { key, label } = dateStr
      ? bucketFor(dateStr, mode)
      : { key: UNKNOWN_KEY, label: 'Unknown date' };
    const bucket = buckets.get(key) ?? { label, runs: [] };
    bucket.runs.push(run);
    buckets.set(key, bucket);
  }
  return [...buckets.entries()]
    .sort(([a], [b]) => {
      if (a === UNKNOWN_KEY) return 1;
      if (b === UNKNOWN_KEY) return -1;
      return b.localeCompare(a);
    })
    .map(([key, { label, runs: groupRunsList }]) => ({
      key,
      label,
      runs: [...groupRunsList].sort(
        (a, b) => Date.parse(ensureUtcIso(b.run_ts ?? '')) - Date.parse(ensureUtcIso(a.run_ts ?? '')),
      ),
    }));
}
